import React, { useState, useEffect } from 'react';
import { QueryBuilder, RuleGroupType, Field, ValueEditorProps } from 'react-querybuilder';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import * as ReactDndTouchBackend from 'react-dnd-touch-backend';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Typography,
  useTheme
} from '@mui/material';
import { FilterAlt as FilterIcon } from '@mui/icons-material';
import { FieldDefinition, SelectOption } from '../AdvancedExpressionBuilder/types';
import { useResourceResolution } from '../../../hooks/useResourceResolution';
import { ruleGroupToMongoDBQuery } from './utils/ruleGroupToMongoDBQuery';
import { OPERATORS_BY_FIELD_TYPE } from './utils/constants';

import 'react-querybuilder/dist/query-builder.css';

interface ASTQueryBuilderProps {
  initialQuery?: RuleGroupType;
  onChange: (mongoQuery: Record<string, any>) => void;
  fields: FieldDefinition[];
  organizationId?: string;
  disabled?: boolean;
}

const operatorsTranslations = [
  { name: 'in', label: 'está em' },
  { name: 'nin', label: 'não está em' },
  { name: 'eq', label: 'é igual a' },
  { name: 'ne', label: 'não é igual a' },
  { name: 'exists', label: 'existe' },
  { name: 'gt', label: 'maior que' },
  { name: 'gte', label: 'maior ou igual a' },
  { name: 'lt', label: 'menor que' },
  { name: 'lte', label: 'menor ou igual a' },
  { name: 'regex', label: 'regex (avançado)' }
];

const getOperatorsForFieldType = (fieldType: string): typeof operatorsTranslations => {
  const operatorNames = OPERATORS_BY_FIELD_TYPE[fieldType] || ['eq', 'ne'];
  return operatorsTranslations.filter(op => operatorNames.includes(op.name));
};

const CustomValueEditor = (props: ValueEditorProps) => {
  const { field, value, handleOnChange, operator, disabled, context } = props;
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SelectOption[]>([]);

  const fieldDef = context?.fieldsData?.find((f: FieldDefinition) => f.identifier === field) as
    | FieldDefinition
    | undefined;

  const { options: resourceOptions, isLoading: resourceLoading } = useResourceResolution({
    dynamicOptions: fieldDef?.components?.dynamicOptions,
    organizationId: context?.organizationId
  });

  useEffect(() => {
    if (fieldDef?.components?.nodes) {
      setOptions(fieldDef.components.nodes);
      return;
    }

    if (fieldDef?.fieldType === 'select' && fieldDef?.components?.dynamicOptions) {
      setOptions(resourceOptions);
      setLoading(resourceLoading);
    }
  }, [fieldDef, resourceOptions, resourceLoading]);

  const handleMultiValueChange = (newValue: string[]) => {
    handleOnChange(newValue.join(', '));
  };

  const parseMultiValue = (value: string | string[]): string[] => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value.map(v => String(v)).filter(v => v);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map(v => v.trim())
        .filter(v => v);
    }

    return [];
  };

  // Helper function to render a basic text field
  const renderTextField = (label = 'Value', type = 'text', startAdornment?: React.ReactNode) => (
    <TextField
      size="small"
      label={label}
      type={type}
      value={value ?? ''}
      onChange={e => handleOnChange(e.target.value)}
      disabled={disabled}
      InputProps={startAdornment ? { startAdornment } : undefined}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      sx={{ minWidth: 300 }}
    />
  );

  // Helper function to render a select field
  const renderSelect = (options: { value: string; label: string }[], label = 'Value') => (
    <FormControl size="small" sx={{ minWidth: 300 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value ?? options[0]?.value}
        onChange={e => handleOnChange(e.target.value)}
        disabled={disabled}
        label={label}>
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Operator-based rendering
  if (operator === 'exists') {
    return renderSelect([
      { value: 'true', label: 'true' },
      { value: 'false', label: 'false' }
    ]);
  }

  if (operator === 'in' || operator === 'nin') {
    const selectedValues = parseMultiValue(value || '');

    if (fieldDef?.components?.nodes || fieldDef?.components?.dynamicOptions) {
      return (
        <Autocomplete
          size="small"
          multiple
          value={selectedValues}
          onChange={(_, newValues) => {
            if (!newValues) return;
            const stringValues = newValues.map(v => String(v));
            handleMultiValueChange(stringValues);
          }}
          options={options.map(opt => opt.value)}
          getOptionLabel={option => {
            const optionData = options.find(opt => opt.value === option);
            return optionData?.label || String(option);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const optionData = options.find(opt => opt.value === option);
              return (
                <Chip
                  variant="outlined"
                  label={optionData?.label || String(option)}
                  size="small"
                  {...getTagProps({ index })}
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              label="Values"
              placeholder="Select values..."
              disabled={disabled}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          disabled={disabled}
          loading={loading}
          sx={{
            minWidth: 300,
            '& .MuiFormControl-root': {
              width: '100%',
              maxWidth: 300
            }
          }}
        />
      );
    }

    return (
      <Autocomplete
        size="small"
        multiple
        freeSolo
        value={selectedValues}
        onChange={(_, newValues) => {
          if (!newValues) return;
          const stringValues = newValues.map(v => String(v));
          handleMultiValueChange(stringValues);
        }}
        options={[]}
        getOptionLabel={option => String(option)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={String(option)}
              size="small"
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={params => (
          <TextField {...params} label="Values" placeholder="Type values..." disabled={disabled} />
        )}
        disabled={disabled}
        sx={{
          minWidth: 300,
          '& .MuiFormControl-root': {
            width: '100%',
            maxWidth: 300
          }
        }}
      />
    );
  }

  // Field type-based rendering
  if (fieldDef?.fieldType === 'date') {
    return renderTextField('Value', 'date');
  }

  if (fieldDef?.fieldType === 'numeric') {
    return renderTextField('Value', 'number');
  }

  if (fieldDef?.fieldType === 'boolean') {
    return renderSelect([
      { value: 'true', label: 'True' },
      { value: 'false', label: 'False' }
    ]);
  }

  if (fieldDef?.fieldType === 'select') {
    // If there are no options available, show a text field for eq/ne operators
    if (options.length === 0 && (operator === 'eq' || operator === 'ne')) {
      return renderTextField('Value', 'text');
    }
    return renderSelect(options, 'Value');
  }
  return renderTextField('Value', 'text');
};

// Custom field selector with search functionality
const CustomFieldSelector = (props: any) => {
  const { value, options, handleOnChange, disabled } = props;

  // Find the selected field to get its label
  const selectedOption = options.find((opt: any) => opt.name === value);

  return (
    <Autocomplete
      size="small"
      value={selectedOption || null}
      onChange={(_, newValue) => {
        handleOnChange(newValue?.name || '');
      }}
      options={options}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.name === value?.name}
      renderInput={params => (
        <TextField
          {...params}
          label="Campo"
          placeholder="Digite para buscar..."
          disabled={disabled}
          size="small"
          sx={{ minWidth: 350 }}
        />
      )}
      disabled={disabled}
      sx={{
        minWidth: 350,
        '& .MuiAutocomplete-option': {
          whiteSpace: 'nowrap',
          textOverflow: 'unset'
        },
        '& .MuiInputBase-input': {
          textOverflow: 'unset',
          whiteSpace: 'nowrap'
        }
      }}
      autoSelect={true}
      clearOnBlur={false}
      clearIcon={false}
      selectOnFocus={false}
      filterOptions={(options, { inputValue }) => {
        if (!inputValue) return options;
        return options.filter(option => {
          return option.label.toLowerCase().includes(inputValue.toLowerCase());
        });
      }}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          // Select first option when Enter is pressed
          const filteredOptions = options.filter((option: any) =>
            option.label
              .toLowerCase()
              .includes((event.target as HTMLInputElement).value.toLowerCase())
          );
          if (filteredOptions.length > 0) {
            handleOnChange(filteredOptions[0].name);
          }
        }
      }}
    />
  );
};

export const ASTQueryBuilder: React.FC<ASTQueryBuilderProps> = ({
  initialQuery,
  onChange,
  fields,
  organizationId,
  disabled = false
}) => {
  const [query, setQuery] = useState<RuleGroupType>(
    initialQuery || {
      combinator: 'and',
      rules: []
    }
  );

  const handleQueryChange = (newQuery: RuleGroupType) => {
    setQuery(newQuery);
  };

  useEffect(() => {
    try {
      const mongoQuery = ruleGroupToMongoDBQuery(query);
      onChange(mongoQuery);
    } catch (error) {
      console.error('Error building MongoDB query:', error);
      onChange({});
    }
  }, [query, onChange]);

  const queryBuilderFields: Field[] = fields.map(field => ({
    name: field.identifier,
    label: field.label || field.identifier,
    valueSource: field.fieldType === 'select' ? 'field' : 'value'
  }));

  const theme = useTheme();

  return (
    <>
      <style>{`
        .queryBuilder .ruleGroup {
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 16px;
          margin-bottom: 16px;
          background-color: #f8f9fa !important;
        }
        .queryBuilder .rule {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding: 8px;
          border-radius: 4px;
        }
        .queryBuilder .ruleGroup-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }
        .queryBuilder .rule {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        
        .queryBuilder .MuiButton-contained {
          background-color: #1B1F27 !important;
          color: #F1F3F5 !important;
        }
        .queryBuilder .MuiButton-contained:hover {
          background-color: #000000 !important;
        }
        
        /* Hide + GROUP button in nested groups */
        .queryBuilder .ruleGroup .ruleGroup .ruleGroup .ruleGroup-addGroup {
          display: none;
        }
        
        /* Override default react-querybuilder blue theme */
        .queryBuilder .ruleGroup {
          background-color: #ffffff !important;
          border: 1px solid #dee2e6 !important;
        }
        .queryBuilder .ruleGroup .ruleGroup {
          background-color: #f8f9fa !important;
          border: 1px solid #e9ecef !important;
        }
        .queryBuilder .ruleGroup .ruleGroup .ruleGroup {
          background-color: #ffffff !important;
          border: 1px solid #dee2e6 !important;
        }
        
        /* Enhanced drag and drop styles */
        .queryBuilder .ruleGroup-dragHandle,
        .queryBuilder .rule-dragHandle {
          cursor: grab;
          color: #666;
          margin-right: 8px;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }
        .queryBuilder .ruleGroup-dragHandle:hover,
        .queryBuilder .rule-dragHandle:hover {
          background-color: rgba(0, 0, 0, 0.1);
        }
        .queryBuilder .ruleGroup-dragHandle:active,
        .queryBuilder .rule-dragHandle:active {
          cursor: grabbing;
        }
        
        /* Drop zone indicators */
        .queryBuilder .ruleGroup.queryBuilder-dnd-over {
          border: 2px dashed #1B1F27 !important;
          background-color: rgba(27, 31, 39, 0.1) !important;
        }
        .queryBuilder .rule.queryBuilder-dnd-over {
          border: 2px dashed #1B1F27 !important;
          background-color: rgba(27, 31, 39, 0.1) !important;
        }
        
        /* Drag preview */
        .queryBuilder .ruleGroup.queryBuilder-dnd-dragging,
        .queryBuilder .rule.queryBuilder-dnd-dragging {
          opacity: 0.5;
          transform: rotate(5deg);
        }
      `}</style>
      <Box className="ast-query-builder queryBuilder">
        <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend, ...ReactDndTouchBackend }}>
          <QueryBuilderMaterial>
            <QueryBuilder
              fields={queryBuilderFields}
              operators={operatorsTranslations}
              query={query}
              onQueryChange={handleQueryChange}
              controlElements={{
                valueEditor: CustomValueEditor,
                fieldSelector: CustomFieldSelector
              }}
              context={{ fieldsData: fields, organizationId: organizationId }}
              disabled={disabled}
              showCloneButtons={true}
              autoSelectField={true}
              enableDragAndDrop={true}
              getOperators={field => {
                const fieldDef = fields.find(f => f.identifier === field);
                return fieldDef ? getOperatorsForFieldType(fieldDef.fieldType) : ['eq', 'ne'];
              }}
            />
          </QueryBuilderMaterial>
        </QueryBuilderDnD>

        {query.rules.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: 'text.secondary'
            }}>
            <Chip
              icon={<FilterIcon />}
              label="No conditions defined yet"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <Typography variant="body1" color="text.secondary">
              Click "Add Rule" to create your first condition
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};
